<template>
    <div :class="props.customClass" :data-cy="props.dataCy">
        <ValidationProvider :name="props.name" :rules="computedRules" v-slot="{ errors }" mode="eager" ref="provider">
            <div class="tw:relative tw:w-full" :data-validation-error="errors[0] ? '' : undefined">
                <div
                    class="input-select-multiselect"
                    :class="{ 'input-select-error': errors[0], 'input-select-disabled': props.disabled }"
                >
                    <multiselect
                        :value="selectedOptions"
                        :options="props.options"
                        :label="props.labelKey"
                        :track-by="props.valueKey"
                        :placeholder="errors[0] ? ' ' : (props.placeholder || ' ')"
                        :disabled="props.disabled"
                        :multiple="props.multiple"
                        :searchable="true"
                        :allow-empty="true"
                        :close-on-select="!props.multiple"
                        :show-labels="false"
                        v-bind="$attrs"
                        @input="handleSelect"
                        @open="isOpen = true"
                        @close="handleClose"
                    />
                </div>
                <span
                    v-if="props.label"
                    class="tw:absolute tw:uppercase tw:duration-200 tw:tracking-wide tw:origin-left tw:px-1 tw:z-[1] tw:start-1.5 tw:pointer-events-none"
                    :class="[
                        (isOpen || !isEmpty)
                            ? 'tw:top-2 tw:-translate-y-4 tw:scale-75 tw:bg-white tw:font-bold'
                            : 'tw:top-1/2 tw:-translate-y-1/2 tw:scale-100 tw:bg-transparent',
                        errors[0]
                            ? (isOpen ? 'tw:text-blue-800 tw:font-bold' : 'tw:text-red-400')
                            : (isOpen ? 'tw:text-blue-800 tw:font-bold' : 'tw:text-slate-400')
                    ]"
                >
                    {{ props.label }}
                </span>
            </div>
            <p class="tw:text-xs tw:m-0 tw:text-slate-400 tw:font-medium">{{ props.description }}</p>
            <span v-if="errors[0]" class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium text-left">
                {{ formatErrorMessage(errors[0]) }}
            </span>

            <span
                v-if="props.apiValidationError && !errors[0]"
                class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
            >
                {{ Array.isArray(props.apiValidationError) ? props.apiValidationError.join(', ') : props.apiValidationError }}
            </span>
        </ValidationProvider>
    </div>
</template>

<script setup>
    import { ref, computed, watch } from 'vue';
    import Multiselect from 'vue-multiselect';

    const props = defineProps({
        label: String,
        name: String,
        rules: [String, Object],
        value: [String, Number, Array],
        options: { type: Array, default: () => [] },
        valueKey: { type: String, default: 'id' },
        labelKey: { type: String, default: 'name' },
        placeholder: String,
        disabled: Boolean,
        multiple: { type: Boolean, default: false },
        apiValidationError: [String, Array],
        description: String,
        customClass: String,
        dataCy: String
    });

    const emit = defineEmits(['input', 'validation-error']);
    const provider = ref(null);
    const isOpen = ref(false);

    const computedRules = computed(() => props.rules || '');

    const localValue = ref(
        props.multiple
            ? (Array.isArray(props.value) ? [...props.value] : [])
            : (props.value != null ? props.value : '')
    );

    const getOptionValue = opt => (typeof opt === 'object' ? opt[props.valueKey] : opt);

    const selectedOptions = computed(() => {
        if (props.multiple) {
            const ids = localValue.value;
            return props.options.filter(o => ids.includes(getOptionValue(o)));
        }
        const val = localValue.value;
        return props.options.find(o => getOptionValue(o) === val) || null;
    });

    const isEmpty = computed(() =>
        props.multiple ? localValue.value.length === 0 : (!localValue.value && localValue.value !== 0)
    );

    const handleClose = async() => {
        isOpen.value = false;
        const result = await provider.value?.validate(selectedOptions.value);
        if (result && !result.valid) {
            emit('validation-error');
        }
    };

    const formatErrorMessage = err => {
        const msg = typeof err === 'string' ? err : (err && err.message) || String(err || '');
        return msg ? msg.charAt(0).toUpperCase() + msg.slice(1).toLowerCase() : '';
    };

    const handleSelect = val => {
        if (props.multiple) {
            const arr = Array.isArray(val) ? val : [];
            const newVal = arr.map(v => getOptionValue(v));
            localValue.value = newVal;
            emit('input', newVal);
        } else {
            const newVal = val ? getOptionValue(val) : null;
            localValue.value = newVal;
            emit('input', newVal);
        }
    };

    watch(() => props.value, newVal => {
        if (props.multiple) {
            localValue.value = Array.isArray(newVal) ? [...newVal] : [];
        } else {
            localValue.value = newVal != null ? newVal : '';
        }
    });
</script>

<script>
    export default {
        inheritAttrs: false,
        components: { Multiselect}
    };
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.input-select-multiselect :deep(.multiselect--active) {
    z-index: auto;
}
.input-select-multiselect :deep(.multiselect__tags) {
    min-height: 38px;
    height: 38px;
    padding: 0.5rem 2rem 0.5rem 0.5rem;
    border-radius: 0.375rem;
    border: 1px solid #f0e2ef;
    background: white;
    margin-bottom: 2px !important;
    display: flex;
    align-items: center;
}
.input-select-multiselect :deep(.multiselect__content-wrapper) {
    border-radius: 0.375rem;
    border: 1px solid #f0e2ee;
    position: absolute;
    z-index: 100;
}
.input-select-multiselect :deep(.multiselect__option) {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    font-size: 14px;
    margin-bottom: 2px;
    border: 1px solid transparent;
}
.input-select-multiselect :deep(.multiselect__option--highlight) {
    background: #ffffff;
    color: #60a5fa;
    border: 1px solid #60a5fa;
    border-radius: 0.375rem;
}
.input-select-multiselect :deep(.multiselect__placeholder) {
    padding: 5px 0 0 0;
    margin: 0;
    line-height: normal;
}
/* Con error y abierto: borde rojo y bg white */
.input-select-multiselect.input-select-error :deep(.multiselect--active .multiselect__tags) {
    border-color: #60a5fa;
    background-color: transparent;
}

/* Sin error y abierto: borde azul */
.input-select-multiselect :deep(.multiselect--active .multiselect__tags) {
    border-color: #60a5fa;
    background-color: transparent;
    color: #60a5fa;
}

/* Sin error y cerrado: borde slate y bg rojo claro */
.input-select-multiselect.input-select-error :deep(.multiselect__tags) {
    border-color: #ef4444;
    background-color: rgba(254, 226, 226, 0.5);
}
.input-select-multiselect :deep(.multiselect__option--selected) {
    color: #ffffff;
    background-color: var(--altcolor);
    border-radius: 0.375rem;
}
/* Opción en hover */
.input-select-multiselect :deep(.multiselect__option--highlight) { 
    color: var(--altcolor);
    background-color: #ffffff;
    border: 1px solid var(--altcolor);
    border-radius: 0.375rem;
}
.input-select-multiselect :deep(.multiselect__option--selected.multiselect__option--highlight) {
    color: #ffffff;
    background-color: var(--altcolor);
    border-radius: 0.375rem;
}
.input-select-multiselect :deep(.multiselect__content) {
    padding: 5px;
    border-radius: 0.375rem;
}
.input-select-multiselect :deep(.multiselect__single) {
    color: inherit;
    margin: 0;
    padding: 0;
    color: #5e6979
}
.input-select-multiselect :deep(.multiselect--active .multiselect__single) {
    color: #60a5fa;
}
.input-select-multiselect.input-select-disabled {
    cursor: not-allowed !important;
}
.input-select-multiselect.input-select-disabled :deep(.multiselect),
.input-select-multiselect.input-select-disabled :deep(.multiselect__tags),
.input-select-multiselect.input-select-disabled :deep(.multiselect__select),
.input-select-multiselect.input-select-disabled :deep(.multiselect__single),
.input-select-multiselect.input-select-disabled :deep(.multiselect__input) {
    cursor: not-allowed !important;
}
.input-select-multiselect :deep(.multiselect--disabled .multiselect__select) {
    background-color: transparent;
}
.input-select-multiselect :deep(.multiselect--disabled) {
    background-color: transparent;
}

</style>
